/**
 * Session detail polls re-sign S3 URLs every few seconds. Comparing full signed
 * strings would remount <video>/clear the user portrait. Identity is the object
 * path (query/hash stripped).
 */

export function archiveObjectIdentity(url: string | null | undefined): string {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    const u = new URL(raw, "https://pulse.local");
    return `${u.origin}${u.pathname}`.replace(/\/+$/, "") || u.pathname;
  } catch {
    return raw.split(/[?#]/)[0] || "";
  }
}

export function sameArchiveObject(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = archiveObjectIdentity(a);
  const right = archiveObjectIdentity(b);
  return Boolean(left) && left === right;
}

/**
 * Download a signed archive object into a same-origin blob URL.
 * Prefer this for short audio blobs only — not for multi-MB camera WebMs
 * (those must use browser Range / progressive `<video preload="auto">`).
 */
export async function fetchArchiveBlobUrl(
  signed: string,
  signal?: AbortSignal,
): Promise<string> {
  const url = String(signed || "").trim();
  if (!url) throw new Error("archive fetch: empty url");
  const res = await fetch(url, { signal, mode: "cors", credentials: "omit" });
  if (!res.ok) throw new Error(`archive fetch ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Seconds of media buffered ahead of `atSec` (0 when nothing covers that point). */
export function mediaBufferedAheadSec(
  el: Pick<HTMLMediaElement, "buffered"> | null | undefined,
  atSec: number,
): number {
  if (!el?.buffered?.length) return 0;
  const t = Math.max(0, Number(atSec) || 0);
  let best = 0;
  for (let i = 0; i < el.buffered.length; i += 1) {
    const start = el.buffered.start(i);
    const end = el.buffered.end(i);
    if (start <= t + 0.05 && end > t) {
      best = Math.max(best, end - t);
    }
  }
  return best;
}

/**
 * Clamp a seek target into already-buffered ranges so progressive S3 WebM
 * does not jump past `bufferedEnd` (that freezes the plate until a full rebuffer).
 */
export function clampMediaTimeToBuffered(
  el: Pick<HTMLMediaElement, "buffered" | "duration"> | null | undefined,
  targetSec: number,
): number {
  const t = Math.max(0, Number(targetSec) || 0);
  if (!el?.buffered?.length) return t;
  for (let i = 0; i < el.buffered.length; i += 1) {
    const start = el.buffered.start(i);
    const end = el.buffered.end(i);
    if (t >= start && t <= end) return t;
  }
  // Prefer the latest buffered end at or before the target.
  let before = -1;
  let after = Number.POSITIVE_INFINITY;
  for (let i = 0; i < el.buffered.length; i += 1) {
    const start = el.buffered.start(i);
    const end = el.buffered.end(i);
    if (end <= t) before = Math.max(before, end);
    if (start >= t) after = Math.min(after, start);
  }
  if (before >= 0) return Math.max(0, before - 0.04);
  if (Number.isFinite(after)) return after;
  return t;
}

/** True when progressive download has enough lead to start the waveform clock. */
export function mediaHasLeadBuffer(
  el: (Pick<HTMLMediaElement, "buffered" | "readyState" | "duration"> & {
    networkState?: number;
  }) | null | undefined,
  atSec = 0,
  leadSec = 1.5,
): boolean {
  if (!el) return false;
  // HAVE_ENOUGH_DATA — browser thinks it can play through without stalling.
  if (el.readyState >= 4) return true;
  const ahead = mediaBufferedAheadSec(el, atSec);
  if (ahead >= leadSec) return true;
  const dur = Number(el.duration);
  if (Number.isFinite(dur) && dur > 0 && ahead >= Math.max(0.4, dur - atSec - 0.2)) {
    return true;
  }
  return false;
}

/** Keep the previously signed URL when the next poll points at the same object. */
export function keepSignedUrl(
  prev: string | null | undefined,
  next: string | null | undefined,
): string | null | undefined {
  if (next == null || next === "") return next;
  if (prev && sameArchiveObject(prev, next)) return prev;
  return next;
}

type MediaUrlFields = {
  timelineAudioUrl?: string;
  audioUrl?: string;
  userAudioUrl?: string;
  botAudioUrl?: string;
  userVideoUrl?: string;
  botVideoUrl?: string;
  videoUrl?: string | null;
  metaUrl?: string;
  transcriptUrl?: string;
  eventsUrl?: string;
  livePerceptionEventsUrl?: string;
  perceptionReportUrl?: string;
  turns?: Array<{ audioUrl?: string; [k: string]: unknown }>;
  segmentAudioUrls?: Array<{ audioUrl?: string; [k: string]: unknown }>;
  transcripts?: Array<{ url?: string; frameUrl?: string; [k: string]: unknown }>;
};

/**
 * Merge a polled session detail onto the in-memory one without flipping media
 * `src` when only the presign query changed.
 */
export function stabilizeSessionDetailMedia<T extends MediaUrlFields>(
  prev: T | null | undefined,
  next: T,
): T {
  if (!prev) return next;
  const out: T = { ...next };
  out.timelineAudioUrl = keepSignedUrl(prev.timelineAudioUrl, next.timelineAudioUrl) as T["timelineAudioUrl"];
  out.audioUrl = keepSignedUrl(prev.audioUrl, next.audioUrl) as T["audioUrl"];
  out.userAudioUrl = keepSignedUrl(prev.userAudioUrl, next.userAudioUrl) as T["userAudioUrl"];
  out.botAudioUrl = keepSignedUrl(prev.botAudioUrl, next.botAudioUrl) as T["botAudioUrl"];
  out.userVideoUrl = keepSignedUrl(prev.userVideoUrl, next.userVideoUrl) as T["userVideoUrl"];
  out.botVideoUrl = keepSignedUrl(prev.botVideoUrl, next.botVideoUrl) as T["botVideoUrl"];
  out.videoUrl = keepSignedUrl(prev.videoUrl, next.videoUrl) as T["videoUrl"];
  out.metaUrl = keepSignedUrl(prev.metaUrl, next.metaUrl) as T["metaUrl"];
  out.transcriptUrl = keepSignedUrl(prev.transcriptUrl, next.transcriptUrl) as T["transcriptUrl"];
  out.eventsUrl = keepSignedUrl(prev.eventsUrl, next.eventsUrl) as T["eventsUrl"];
  out.livePerceptionEventsUrl = keepSignedUrl(
    prev.livePerceptionEventsUrl,
    next.livePerceptionEventsUrl,
  ) as T["livePerceptionEventsUrl"];
  out.perceptionReportUrl = keepSignedUrl(
    prev.perceptionReportUrl,
    next.perceptionReportUrl,
  ) as T["perceptionReportUrl"];

  if (Array.isArray(next.turns) && Array.isArray(prev.turns)) {
    out.turns = next.turns.map((turn, i) => {
      const prior = prev.turns?.[i];
      if (!prior || !turn?.audioUrl) return turn;
      return { ...turn, audioUrl: keepSignedUrl(prior.audioUrl, turn.audioUrl) as string };
    });
  }
  if (Array.isArray(next.segmentAudioUrls) && Array.isArray(prev.segmentAudioUrls)) {
    out.segmentAudioUrls = next.segmentAudioUrls.map((seg, i) => {
      const prior = prev.segmentAudioUrls?.[i];
      if (!prior || !seg?.audioUrl) return seg;
      return { ...seg, audioUrl: keepSignedUrl(prior.audioUrl, seg.audioUrl) as string };
    });
  }
  if (Array.isArray(next.transcripts) && Array.isArray(prev.transcripts)) {
    out.transcripts = next.transcripts.map((row, i) => {
      const prior = prev.transcripts?.[i];
      if (!prior) return row;
      return {
        ...row,
        url: keepSignedUrl(prior.url, row.url) as typeof row.url,
        frameUrl: keepSignedUrl(prior.frameUrl, row.frameUrl) as typeof row.frameUrl,
      };
    });
  }
  return out;
}
